// Use caminho relativo por padrão para evitar CORS em produção
const API_URL = import.meta.env.VITE_API_URL || '/api';
const API_TOKEN = import.meta.env.VITE_API_TOKEN || '';

interface RequestConfig extends RequestInit {
  params?: Record<string, string | number | boolean>;
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const { params, headers, ...restConfig } = config;

    let url = `${this.baseURL}${endpoint}`;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });
      url += `?${searchParams.toString()}`;
    }

    const token = this.getToken();
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(API_TOKEN && { 'X-API-Token': API_TOKEN }),
    };

    try {
      const response = await fetch(url, {
        ...restConfig,
        credentials: 'include', // Envia cookies entre domínios para autenticação Sanctum
        headers: {
          ...defaultHeaders,
          ...headers,
        },
      });

      const contentType = response.headers.get('content-type') || '';

      if (!response.ok) {
        if (response.status === 401) {
          // NÃO remove token nem redireciona — deixa o chamador decidir
          const err: any = new Error('Sessão expirada. Faça login novamente.');
          err.status = 401;
          throw err;
        }

        // Tenta extrair JSON; se vier HTML/texto, captura e cria erro descritivo
        if (!contentType.includes('application/json')) {
          const text = await response.text().catch(() => '');
          const snippet = text ? text.slice(0, 200) : '';
          const err: any = new Error(
            'A API retornou conteúdo não-JSON (verifique VITE_API_URL, proxy /api e CORS). ' +
              (snippet ? `Prévia: ${snippet}` : '')
          );
          err.status = response.status;
          throw err;
        }
        const errorData = await response.json().catch(() => ({}));
        const err: any = new Error((errorData && (errorData as any).message) || 'Erro na requisição');
        err.status = response.status;
        // Anexa estrutura de erros do backend (ex: { errors: { description: ['...'] } })
        if (errorData && typeof errorData === 'object') {
          err.errors = (errorData as any).errors;
          err.data = errorData;
        }
        throw err;
      }

      // DELETE/No Content ou resposta sem corpo
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return undefined as T;
      }
      if (!contentType.includes('application/json')) {
        const text = await response.text().catch(() => '');
        const snippet = text ? text.slice(0, 200) : '';
        throw new Error(
          'A API retornou conteúdo não-JSON (verifique VITE_API_URL, proxy /api e CORS). ' +
            (snippet ? `Prévia: ${snippet}` : '')
        );
      }
      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erro desconhecido');
    }
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T | void> {
    const { params, headers, ...restConfig } = config || {};

    let url = `${this.baseURL}${endpoint}`;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, String(value));
      });
      url += `?${searchParams.toString()}`;
    }

    const token = this.getToken();
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(API_TOKEN && { 'X-API-Token': API_TOKEN }),
    };

    const response = await fetch(url, {
      ...restConfig,
      credentials: 'include', // Envia cookies entre domínios para autenticação Sanctum
      method: 'DELETE',
      headers: {
        ...defaultHeaders,
        ...headers,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        const err: any = new Error('Sessão expirada. Faça login novamente.');
        err.status = 401;
        throw err;
      }

      const errorData = await response.json().catch(() => ({}));
      const err: any = new Error(errorData.message || 'Erro na requisição');
      err.status = response.status;
      if (errorData && typeof errorData === 'object') {
        err.errors = errorData.errors;
        err.data = errorData;
      }
      throw err;
    }

    // DELETE geralmente retorna 204 No Content
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await response.text().catch(() => '');
      const snippet = text ? text.slice(0, 200) : '';
      throw new Error(
        'A API retornou conteúdo não-JSON (verifique VITE_API_URL, proxy /api e CORS). ' +
          (snippet ? `Prévia: ${snippet}` : '')
      );
    }
    return await response.json();
  }

  async uploadFile<T>(endpoint: string, file: File, additionalData?: Record<string, string>): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const token = this.getToken();
    const headers: HeadersInit = {
      'Accept': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(API_TOKEN && { 'X-API-Token': API_TOKEN }),
    };

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      credentials: 'include', // Envia cookies entre domínios para autenticação Sanctum
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Erro no upload');
    }

    return await response.json();
  }
}

export const api = new ApiClient(API_URL);
